/**
 * bindEngine(store, engine): one-way sync from the Pinia store to a ViewerEngine plus the engine's hover/pick
 * events back into the store. The engine is called in a deterministic order so packages/render can diff:
 *   setSkybox, setTool, then per added/replaced entry setEntry, per removed entry removeEntry, per frame change
 *   setCurrentModel, then setComponents (full list) and setSelection.
 * Components are passed as fresh shallow copies on every change (the engine diffs by JSON of filters and
 * visualizations and keeps GPU state by serial). When the engine accepts a filter evaluator, the store's
 * per-component atom cache (backed by the data worker) is installed as that evaluator.
 * Returns an unbind function. Selection semantics live in store.applyPick (default tool toggles the whole
 * residue, selection tool Atom/Residue/Chain, empty click clears, shift extends).
 */
import { watch, type WatchStopHandle } from 'vue';
import type { ViewerEngine } from './engine';
import type { LiteStore } from './store';
import type { Entry } from './types';

export interface EngineBinding {
  engine: ViewerEngine;
  unbind(): void;
  /** Force a full resync (used after the engine signals `ready` or after context loss). */
  resync(): void;
}

export function bindEngine(store: LiteStore, engine: ViewerEngine): EngineBinding {
  const stops: WatchStopHandle[] = [];
  const offs: Array<() => void> = [];
  /** entrySerial -> the model object last pushed, to detect replacement */
  const pushed = new Map<number, { model: unknown; modelSerial: number }>();

  store.setEngine(engine);
  engine.setFilterEvaluator?.((component, entrySerial) => store.componentAtomsFor(component, entrySerial));

  function pushEnvironment() {
    engine.setSkybox(store.view.skyboxTheme, store.view.nightMode);
    engine.setTool(store.view.tool, store.view.selectionMode);
  }

  function pushEntries(current: Entry[]) {
    const live = new Set<number>();
    for (const entry of current) {
      live.add(entry.serial);
      const model = store.models.get(entry.serial);
      if (!model) continue;
      const prev = pushed.get(entry.serial);
      if (!prev || prev.model !== model) {
        engine.setEntry(entry, model);
        pushed.set(entry.serial, { model, modelSerial: entry.currentModelSerial });
        if (entry.currentModelSerial !== 1) engine.setCurrentModel(entry.serial, entry.currentModelSerial);
      }
      else if (prev.modelSerial !== entry.currentModelSerial) {
        engine.setCurrentModel(entry.serial, entry.currentModelSerial);
        prev.modelSerial = entry.currentModelSerial;
      }
    }
    for (const serial of [...pushed.keys()]) {
      if (live.has(serial)) continue;
      engine.removeEntry(serial);
      pushed.delete(serial);
    }
  }

  function pushComponents() { engine.setComponents(store.components.map(c => ({ ...c }))); }
  function pushSelection() { engine.setSelection({ ...store.selection.byEntry }); }

  function resync() {
    pushed.clear();
    pushEnvironment();
    pushEntries(store.entries);
    pushComponents();
    pushSelection();
  }

  // initial push, then watchers (flush 'sync' keeps the call order deterministic for tests and the renderer)
  resync();
  stops.push(
    watch(() => [store.view.skyboxTheme, store.view.nightMode] as const, ([t, n]) => engine.setSkybox(t, n)),
    watch(() => [store.view.tool, store.view.selectionMode] as const, ([t, m]) => engine.setTool(t, m)),
    watch(() => store.entries.map(e => `${e.serial}:${e.currentModelSerial}:${store.models.get(e.serial) ? 1 : 0}`).join('|'), () => pushEntries(store.entries)),
    watch(() => store.components, () => pushComponents(), { deep: true }),
    watch(() => store.selection, () => pushSelection(), { deep: true }),
  );

  offs.push(
    engine.on('hover', info => store.setHover(info)),
    engine.on('pick', (result, ev) => store.applyPick(result, ev)),
    engine.on('ready', () => { store.engineReady = true; resync(); }),
    engine.on('error', message => store.toast('error', 'Renderer error', message)),
  );

  return {
    engine,
    resync,
    unbind() {
      for (const s of stops) s();
      for (const o of offs) o();
      engine.setFilterEvaluator?.(null);
      if (store.engine === engine) store.setEngine(null);
    },
  };
}
